/**
 * WebSocket Message Handlers
 * Handles all incoming WebSocket message types for the sync protocol.
 * Extracted from websocket.js for better separation of concerns.
 *
 * Uses the factory pattern with dependency injection (same as ws-heartbeat.js etc.).
 */

import { reportSyncPause, clearSyncPause, canResumeSync } from './sync-pause.js';
import { recordQuotaRejection, clearQuotaRejection } from '../services/quota-state.js';
import { isQuotaError } from './server-errors.js';
import { MessageType, ConnectionState, decodeBytes } from "./ws-protocol.js";
import { decryptPayload } from "./ws-encryption.js";
import { announceUpdate } from "./app-version.js";
import * as StorageHealth from "./storage-health.js";
import { isCompactionNeededError, isCoverageRefusal, isDurabilityBlockingServerError, isPermissionDeniedError, isServerBusy, isTransientServerError } from "./server-errors.js";

/**
 * Decrypt a sync payload. Throws if no encryption key is set —
 * encryption is always required, no plaintext fallback.
 * @param {string} base64Data - Base64 encoded encrypted data
 * @param {CryptoKey} encryptionKey - AES-GCM key (required)
 * @param {string} docId - Document ID used as AAD
 * @returns {Promise<Uint8Array>}
 */
async function decryptPayloadWithFallback(base64Data, encryptionKey, docId) {
  if (!encryptionKey) {
    throw new Error("[SyncManager] Cannot decrypt sync data: encryption key not set");
  }

  return decryptPayload(base64Data, encryptionKey, docId);
}

/**
 * Create a message handler for incoming WebSocket messages.
 *
 * @param {Object} options
 * @param {Function} options.getDocId - Returns current document ID
 * @param {Function} options.getDocManager - Returns the Yjs document manager
 * @param {Function} options.getEncryptionKey - Returns the current encryption key (CryptoKey|null)
 * @param {Function} options.getCompaction - Returns the compaction manager
 * @param {Function} options.getHeartbeat - Returns the heartbeat manager
 * @param {Function} options.setState - Transition connection state
 * @param {Function} options.startCompactionTimer - Start compaction after initial sync
 * @param {Function} options.disconnect - Disconnect the WebSocket
 * @param {Object} options.callbacks - Event callbacks
 * @param {Function} [options.callbacks.onPresenceChange] - Presence update callback
 * @param {Function} [options.callbacks.onError] - Error callback
 * @param {Function} [options.callbacks.onRemoteUpdate] - Remote update callback
 * @param {Function} [options.callbacks.onDeltaAck] - Delta ack callback
 * @param {Function} [options.callbacks.onUnshared] - Unshared callback
 * @param {Function} [options.callbacks.onAutoUnshared] - Auto-unshared callback
 * @param {Function} [options.callbacks.onDekRotated] - DEK rotation callback (shareId) => void
 * @param {Function} options.log - Logging function
 * @returns {{ handleMessage: Function }}
 */
export function createMessageHandler(options) {
  const {
    getDocId,
    getDocManager,
    getEncryptionKey,
    getPreviousKeys,
    getCompaction,
    getHeartbeat,
    setState,
    startCompactionTimer,
    disconnect,
    callbacks,
    log,
  } = options;

  /**
   * Decrypt helper using current encryption key
   */
  async function decrypt(base64Data) {
    try {
      return await decryptPayloadWithFallback(base64Data, getEncryptionKey(), getDocId());
    } catch (err) {
      // A member may have sent an update with the previous key just before
      // a rotation reached them: try the keys this session used before.
      for (const key of getPreviousKeys?.() || []) {
        try {
          return await decryptPayload(base64Data, key, getDocId());
        } catch { /* try the next one */ }
      }
      throw err;
    }
  }

  /**
   * Decrypt with a single self-heal retry. AES-GCM auth-tag failure
   * means our DEK doesn't match the ciphertext on the server, which
   * happens when a DEK rotation completed server-side but the client's
   * local vault never picked up the new wrapped DEK (e.g., the rotation
   * caller crashed between server-commit and updateDocWrappedDEK, or
   * the local copy is from a stale snapshot). On failure we ask the
   * orchestrator (via onKeyOutOfSync) to refresh the key from the
   * server's authoritative `doc_keys` row; if it does, we retry once.
   */
  async function decryptWithRefresh(base64Data) {
    try {
      return await decrypt(base64Data);
    } catch (firstErr) {
      if (!callbacks.onKeyOutOfSync) throw firstErr;
      let refreshed;
      try {
        refreshed = await callbacks.onKeyOutOfSync();
      } catch (refreshErr) {
        console.warn("[SyncManager] Key refresh failed:", refreshErr);
        throw firstErr;
      }
      if (!refreshed) throw firstErr; // server's key matches what we already had
      return await decrypt(base64Data);
    }
  }

  /**
   * Handle Rust enum-style "Unshared" message
   */
  function handleRustUnshared(shareInfo) {
    log("Document unshared (enum format):" + JSON.stringify(shareInfo));
    const currentState = getDocManager()?.getEncodedState?.();
    disconnect();
    if (callbacks.onUnshared) {
      callbacks.onUnshared({
        docId: getDocId(),
        shareId: shareInfo.share_id,
        title: shareInfo.title,
        state: currentState,
      });
    } else if (callbacks.onError) {
      callbacks.onError({
        type: "unshared",
        message: "This document is no longer shared",
      });
    }
  }

  /**
   * Handle Rust enum-style "AutoUnshared" message
   */
  function handleRustAutoUnshared(shareInfo) {
    log("Share auto-stopped (0 users):" + JSON.stringify(shareInfo));
    if (callbacks.onAutoUnshared) {
      callbacks.onAutoUnshared({
        docId: getDocId(),
        shareId: shareInfo.share_id,
        title: shareInfo.title,
      });
    }
  }

  /**
   * Handle sync-step2 message (initial sync response)
   */
  async function handleSyncStep2(message) {
    const docManager = getDocManager();

    // New format: { baseline?, deltas[] } - baseline may be omitted when null
    // Legacy format: { updates }
    // Note: Server uses skip_serializing_if for baseline, so check for deltas too
    const serverHadData = !!(message.baseline || (message.deltas && message.deltas.length > 0) || message.updates);
    let decryptedAny = false;
    let incomplete = false;
    let baselineFailed = false;
    // Every server update we successfully decrypted+applied. Used after sync
    // to compute the server's state vector and push back any local state the
    // server is missing (offline edits from a previous session, IndexedDB
    // ahead of server). The server discards our sync-step1 state vector, so
    // without this client-side reconciliation, client-ahead state is only
    // pushed by the next compaction — i.e., possibly never (see audit A1).
    const appliedServerUpdates = [];
    // Coverage (which stored rows this client's state includes) restarts
    // with each full sync.
    callbacks.onSyncStep2Start?.();
    // Whether this server reads messages sent in parts (ws-parts.js).
    callbacks.onServerReadsParts?.(message.parts === true);

    if ("deltas" in message || "baseline" in message) {
      // New delta sync format
      log(
        "Received sync-step2 (delta format), baseline:" +
          !!message.baseline +
          " deltas:" +
          (message.deltas?.length || 0),
      );

      // Apply baseline first. Use decryptWithRefresh so a stale local DEK
      // (rotation hole) self-heals from server's `doc_keys` row instead of
      // permanently aborting sync with "CRITICAL: Baseline decryption failed".
      if (message.baseline) {
        try {
          const baseline = await decryptWithRefresh(message.baseline);
          log("Applying baseline, size:" + baseline?.length);
          docManager.applyUpdate(baseline);
          decryptedAny = true;
          appliedServerUpdates.push(baseline);
          await callbacks.onPayloadApplied?.(message.baseline);
        } catch (decryptErr) {
          await callbacks.onPayloadUnreadable?.(message.baseline);
          console.error(
            "[SyncManager] Failed to decrypt baseline (after key-refresh attempt):",
            decryptErr,
          );
          baselineFailed = true;
        }
      }

      // Apply deltas in order (only if baseline succeeded or was absent)
      let skippedDeltas = 0;
      if (baselineFailed) {
        log("Skipping deltas because baseline decryption failed — deltas without their baseline would produce incomplete state");
      } else if (message.deltas && message.deltas.length > 0) {
        log(`Applying ${message.deltas.length} deltas`);
        for (let i = 0; i < message.deltas.length; i++) {
          try {
            const delta = await decrypt(message.deltas[i]);
            docManager.applyUpdate(delta);
            decryptedAny = true;
            appliedServerUpdates.push(delta);
            await callbacks.onPayloadApplied?.(message.deltas[i]);
          } catch (decryptErr) {
            await callbacks.onPayloadUnreadable?.(message.deltas[i]);
            console.error(
              `[SyncManager] Failed to decrypt delta ${i + 1}, skipping:`,
              decryptErr,
            );
            skippedDeltas++;
            // Continue with remaining deltas - Yjs is resilient
          }
        }
      }

      // Reset delta counter after full sync
      getCompaction()?.resetDeltaCount();

      // DATA-LOSS GUARD: if any server delta was skipped, this client's
      // local state is incomplete. A compaction from this client would
      // replace the server's delta rows with a full state lacking the
      // skipped content — permanent loss unless another live client
      // re-pushes it. Block compaction until a clean sync clears it.
      if (skippedDeltas > 0) {
        incomplete = true;
        console.error(
          `[SyncManager] ${skippedDeltas} server delta(s) could not be decrypted — compaction disabled to protect server-side data`,
        );
        getCompaction()?.suppress(`${skippedDeltas} undecryptable server delta(s) skipped during sync`);
      } else {
        getCompaction()?.unsuppress();
      }
    } else if (message.updates) {
      // Legacy format: single full state
      log(
        "Received sync-step2 (legacy format), has updates:" +
          !!message.updates,
      );
      try {
        const updates = await decrypt(message.updates);
        log("Applying sync-step2 updates, size:" + updates?.length);
        docManager.applyUpdate(updates);
        decryptedAny = true;
        appliedServerUpdates.push(updates);
      } catch (decryptErr) {
        console.error(
          "[SyncManager] Failed to decrypt sync-step2 updates:",
          decryptErr,
        );
        console.log(
          "[SyncManager] This might be old data encrypted with a different key. Skipping...",
        );
      }
    }

    // SAFETY: If the server sent data but we couldn't decrypt ANY of it,
    // or if the baseline failed (deltas without their baseline produce
    // incomplete state), do NOT mark as synced or allow compaction — that
    // would overwrite good server data with our empty/stale local state.
    if (serverHadData && (!decryptedAny || baselineFailed || incomplete)) {
      const reason = baselineFailed
        ? "Baseline decryption failed — cannot apply deltas without baseline"
        : "One or more server updates could not be read";
      console.error(`[SyncManager] CRITICAL: ${reason}. Aborting sync to prevent data loss.`);
      reportSyncPause('map', getDocId(), 'unreadable');
      setState(ConnectionState.ERROR);
      return;
    }

    // Check for piggybacked version info
    if (message.version && message.update_level) {
      log(
        "Version info piggybacked on sync-step2:" +
          message.version +
          " level:" +
          message.update_level,
      );
      announceUpdate({
        version: message.version,
        level: message.update_level,
        message: null,
      });
    }

    if (!canResumeSync('map', getDocId())) {
      setState(ConnectionState.ERROR);
      return;
    }
    clearSyncPause('map', getDocId());

    // Reaching here means the current key read the server's baseline: sends
    // held back since a rotation notice may use it.
    callbacks.onKeyConfirmed?.();

    // Map history: the editing session the server records this socket's
    // changes under (none from an older server, or for a reader).
    docManager?.setEditSession?.(typeof message.edit_session === "number" ? message.edit_session : null);

    // Start compaction timer after initial sync
    startCompactionTimer();
    setState(ConnectionState.SYNCED);
    log("Initial sync complete");

    // Client-ahead reconciliation: if local state contains edits the server
    // doesn't have (offline edits from a prior session that never flushed,
    // IndexedDB ahead of server), push the diff now. Fire-and-forget — the
    // doc is already SYNCED and usable; the push is a normal tracked delta.
    if (callbacks.onSyncStep2Applied) {
      Promise.resolve(callbacks.onSyncStep2Applied(appliedServerUpdates)).catch((err) => {
        console.error("[SyncManager] Client-ahead reconciliation failed:", err);
      });
    }
  }

  /**
   * Handle remote-update message
   */
  async function handleRemoteUpdate(message) {
    log("Received remote-update from:" + message.from);
    if (!message.payload) return;

    // Decrypt step. Real failures here mean a wrong key or a malformed
    // payload — skip the update.
    let update;
    try {
      // With refresh: the update may use a key rotated while this client
      // missed the notice.
      update = await decryptWithRefresh(message.payload);
    } catch (decryptErr) {
      await callbacks.onPayloadUnreadable?.(message.payload);
      console.error(
        "[SyncManager] Failed to decrypt remote update:",
        decryptErr,
      );
      console.log(
        "[SyncManager] Unreadable update from:",
        message.from,
      );
      getCompaction()?.suppress('Unreadable live update');
      reportSyncPause('map', getDocId(), 'unreadable');
      setState(ConnectionState.ERROR);
      return;
    }

    log("Applying remote update, size:" + update?.length);
    await callbacks.onPayloadApplied?.(message.payload);

    // Apply step. Y.applyUpdate() runs internal observers synchronously,
    // including IndexeddbPersistence._storeUpdate. If the IDB connection
    // is closing — typically during a doc switch, tab unload, or after
    // an explicit close — that observer throws InvalidStateError /
    // "database connection is closing". The in-memory Y.Doc has already
    // been updated by that point, so this is NOT a decrypt failure and
    // the update is not "malformed". Distinguish here so the log doesn't
    // suggest data loss every time the user changes docs.
    try {
      getDocManager().applyUpdate(update);
      callbacks.onRemoteApplied?.(update);
    } catch (applyErr) {
      const msg = String(applyErr?.message || "");
      const isIdbClosing =
        applyErr?.name === "InvalidStateError"
        || /database connection is closing|backing store/i.test(msg);
      if (isIdbClosing) {
        callbacks.onRemoteApplied?.(update); // in the copy in memory all the same
        console.warn(
          "[SyncManager] applyUpdate: IDB persistence write skipped (connection closing). In-memory state is current; next compaction will re-persist.",
          applyErr?.message || applyErr,
        );
      } else {
        console.error("[SyncManager] applyUpdate failed post-decrypt:", applyErr);
      }
    }

    if (callbacks.onRemoteUpdate) {
      callbacks.onRemoteUpdate({
        from: message.from,
        timestamp: message.timestamp,
      });
    }
  }

  /**
   * Handle presence message
   */
  function handlePresence(message) {
    if (callbacks.onPresenceChange) {
      callbacks.onPresenceChange(message.users || []);
    }
  }

  /**
   * Handle update-available message
   */
  function handleUpdateAvailable(message) {
    log(
      "Update available:" + message.version + " level:" + message.level,
    );
    announceUpdate({
      version: message.version,
      level: message.level,
      message: message.message,
    });
  }

  /**
   * Handle unshared message (typed format)
   */
  function handleUnshared(message) {
    log("Document unshared:" + JSON.stringify(message));
    // Get current doc state before disconnecting (for conversion to local map)
    const currentState = getDocManager()?.getEncodedState?.();
    disconnect();
    if (callbacks.onUnshared) {
      callbacks.onUnshared({
        docId: getDocId(),
        shareId: message.share_id,
        title: message.title,
        state: currentState,
        hasSnapshot: message.has_snapshot,
        snapshotId: message.snapshot_id,
      });
    } else if (callbacks.onError) {
      callbacks.onError({
        type: "unshared",
        message: "This document is no longer shared",
      });
    }
  }

  /**
   * Handle removed message
   */
  function handleRemoved(message) {
    log("Removed from share:" + JSON.stringify(message));
    const stateBeforeRemoval = getDocManager()?.getEncodedState?.();
    disconnect();
    if (callbacks.onUnshared) {
      callbacks.onUnshared({
        docId: getDocId(),
        shareId: message.share_id,
        state: stateBeforeRemoval,
        reason: "removed",
        hasSnapshot: message.has_snapshot,
        snapshotId: message.snapshot_id,
      });
    } else if (callbacks.onError) {
      callbacks.onError({
        type: "removed",
        message: "You no longer have access to this document",
      });
    }
  }

  /**
   * Handle dek-rotated message: share DEK was rotated, reconnect with new key
   */
  async function handleDekRotated() {
    const shareId = getDocId();
    log("DEK rotated for share: " + shareId);
    // Hold every send from now on: anything encrypted with the old key after
    // the rotation would be unreadable for members who only get the new one.
    callbacks.onDekRotationNotice?.();
    if (callbacks.onDekRotated) {
      await callbacks.onDekRotated(shareId);
    }
  }

  /**
   * Handle compaction-ack message
   */
  function handleCompactionAck(message) {
    log(`Compaction ack: cleared=${message.deltas_cleared} deltas`);
    getCompaction()?.onAck();
    clearQuotaRejection(getDocId());
    callbacks.onCompactionAck?.();
  }

  /**
   * Handle delta-ack message
   */
  function handleDeltaAck(message) {
    if (callbacks.onDeltaAck) {
      callbacks.onDeltaAck(message.deltaId);
    }
    // The map holds many changes since its last compaction: the server asks
    // this writer (one at a time) to compact before the cap is reached.
    if (message.compact) callbacks.onCompactionRequested?.();
  }

  /**
   * Handle heartbeat-ack message
   */
  function handleHeartbeatAck() {
    getHeartbeat()?.onAck();
    callbacks.onHeartbeatAck?.();
  }

  /**
   * Handle error message
   */
  function handleError(message) {
    // Busy (backend save_gate.rs): nothing stored, nothing wrong; the change
    // (or the snapshot) is sent again after a pause.
    if (isServerBusy(message)) {
      console.warn("[SyncManager] Server busy; sending again shortly:", message.deltaId || "snapshot");
      if (!message.deltaId) getCompaction()?.onBusy?.(message.message);
      callbacks.onServerBusy?.({ deltaId: message.deltaId, retryAfterMs: message.retryAfterMs });
      return;
    }
    console.error("[SyncManager] Server error:", message.message);
    // S-M10: the snapshot did not cover every stored update. Nothing was
    // changed on the server; sync again (fresh coverage) and retry later.
    if (isCoverageRefusal(message.message)) {
      getCompaction()?.onCoverageRejected?.(message.message);
      callbacks.onResyncNeeded?.();
      return;
    }
    if (isCompactionNeededError(message.message)) {
      // Too many changes since the map's last compaction (all editors
      // together): compact now. The snapshot carries the refused change,
      // which is in the local copy. Not a storage failure, nor a failed
      // compaction.
      callbacks.onCompactionNeeded?.(message.message);
      return;
    }
    getCompaction()?.onServerError(message.message);
    if (isQuotaError(message.message)) {
      callbacks.onQuotaError?.();
      recordQuotaRejection(getDocId());
      return;
    }
    if (isTransientServerError(message.message)) {
      // Recoverable (e.g. rate-limit during a burst): clear pending acks so
      // the 30s timeout doesn't lock the gate later. The rejected delta is
      // still in local Yjs + IndexedDB and is pushed by the client-ahead
      // reconciliation after the next sync-step2 (onSyncStep2Applied), or by
      // the next compaction — whichever comes first.
      callbacks.onTransientError?.(message.message);
    } else if (isPermissionDeniedError(message.message)) {
      // A viewer's write on this map: stop writing on this channel only.
      callbacks.onPermissionDenied?.(message.message);
    } else if (isDurabilityBlockingServerError(message.message)) {
      StorageHealth.markBroken(`server_rejected: ${message.message}`);
    }
    if (callbacks.onError) {
      callbacks.onError({ type: "server", message: message.message });
    }
  }

  /**
   * Main message dispatcher - handles all incoming WebSocket messages.
   * @param {string} data - Raw JSON string from WebSocket
   */
  async function handleMessage(data) {
    try {
      const message = JSON.parse(data);

      // Debug: log incoming message type only (no payload — encrypted data must not leak to console)
      if (message.type !== MessageType.HEARTBEAT_ACK) {
        console.log(
          "[SyncManager] Received message:",
          message.type || Object.keys(message)[0],
        );
      }

      // Handle Rust enum-style messages (e.g., {"Unshared": {...}})
      if (message.Unshared) {
        handleRustUnshared(message.Unshared);
        return;
      }

      if (message.AutoUnshared) {
        handleRustAutoUnshared(message.AutoUnshared);
        return;
      }

      switch (message.type) {
        case MessageType.SYNC_STEP_2:
          await handleSyncStep2(message);
          break;

        case MessageType.REMOTE_UPDATE:
          await handleRemoteUpdate(message);
          break;

        case MessageType.PRESENCE:
          handlePresence(message);
          break;

        case MessageType.UPDATE_AVAILABLE:
          handleUpdateAvailable(message);
          break;

        case MessageType.UNSHARED:
          handleUnshared(message);
          break;

        case MessageType.REMOVED:
          handleRemoved(message);
          break;

        case MessageType.DEK_ROTATED:
          await handleDekRotated();
          break;

        case MessageType.COMPACTION_ACK:
          handleCompactionAck(message);
          break;

        case MessageType.DELTA_ACK:
          handleDeltaAck(message);
          break;

        case MessageType.HEARTBEAT_ACK:
          handleHeartbeatAck();
          break;

        case MessageType.ERROR:
          handleError(message);
          break;

        case MessageType.SESSION_INVALIDATED:
          // The server closes the socket right after this; the reconnect
          // re-evaluates write access. Let the orchestrator refresh state
          // that depends on why (e.g. our role changed).
          callbacks.onSessionInvalidated?.(message.reason);
          break;

        case MessageType.RESYNC:
          // The server's broadcast ring overwrote messages before this
          // socket read them: sync again here (the stored state holds them).
          console.warn("[SyncManager] Missed changes on this connection; syncing again:", message.reason);
          callbacks.onResyncNeeded?.();
          break;

        default:
          console.warn("[SyncManager] Unknown message type:", message.type);
      }
    } catch (error) {
      console.error("[SyncManager] Error handling message:", error);
    }
  }

  return { handleMessage };
}
